import { OpusAgentChatTurnStatus } from "@src/types.generated";

// Structural payload shape shared by the fragment codegen emits and by
// tests — mirrors the server's OpusAgentEventPayload. Exactly one kind is
// set per payload; a payload where all are null is a kind this client
// predates (render fallbackText, else skip).
export type OpusAgentEventPayloadLike = {
  assistantText?: { text: string } | null;
  thinking?: { text: string } | null;
  toolCall?: {
    callId?: string | null;
    toolName: string;
    label: string;
    status: string;
    // Report name / path / pattern, when the relay attached one.
    detail?: string | null;
  } | null;
  planUpdate?: {
    done: number;
    total: number;
    activeStep?: string | null;
    // Absent on payloads written before items existed.
    items?: { text: string; status: string }[] | null;
  } | null;
  error?: { message: string } | null;
  // The agent condensed earlier conversation to stay within its context
  // limits; the client shows a quiet activity row (no numbers).
  contextCompacted?: { summaryChars: number } | null;
  fallbackText?: string | null;
};

export type OpusAgentEventLike = {
  index: number;
  payload: OpusAgentEventPayloadLike;
};

export type OpusAgentEventKind =
  | "assistantText"
  | "thinking"
  | "toolCall"
  | "planUpdate"
  | "error"
  | "contextCompacted"
  | "unknown";

export const getEventKind = (
  payload: OpusAgentEventPayloadLike,
): OpusAgentEventKind => {
  if (payload.assistantText) return "assistantText";
  if (payload.thinking) return "thinking";
  if (payload.toolCall) return "toolCall";
  if (payload.planUpdate) return "planUpdate";
  if (payload.error) return "error";
  if (payload.contextCompacted) return "contextCompacted";
  return "unknown";
};

// Text for a tool row: `toolName`, or `toolName:detail` when the relay
// attached what the call is about. NOT shortened here — the row fills the
// drawer's real width and CSS ellipsises what does not fit, so the text
// grows with a wider drawer instead of being cut to a character budget
// guessed against its narrowest size. The full value is the same string;
// the row shows it in a tooltip.
export const toolRowLabel = (
  toolName: string,
  detail: string | null | undefined,
): string => (detail ? `${toolName}:${detail}` : toolName);

// Plan item statuses (deepagents' write_todos vocabulary); anything else
// reads as pending.
export type PlanItemStatus = "pending" | "in_progress" | "completed";

export const planItemStatus = (status: string): PlanItemStatus =>
  status === "completed" || status === "in_progress" ? status : "pending";

// Text-like blocks stand alone in the turn; every other kind is a compact
// activity row. Consecutive rows form one group so the column can space
// group ↔ text seams like paragraphs while rows inside a group stay tight.
export const isActivityRowKind = (kind: OpusAgentEventKind): boolean =>
  kind !== "assistantText" && kind !== "thinking" && kind !== "error";

export type EventBlockGroup<T extends OpusAgentEventLike> =
  | { kind: "rows"; key: string; events: T[] }
  | { kind: "block"; key: string; event: T };

// Keys come from the event index (unique within a turn; a row group takes
// its first event's).
export const groupEventBlocks = <T extends OpusAgentEventLike>(
  events: T[],
): EventBlockGroup<T>[] => {
  const groups: EventBlockGroup<T>[] = [];
  for (const event of events) {
    const last = groups[groups.length - 1];
    if (isActivityRowKind(getEventKind(event.payload))) {
      if (last?.kind === "rows") last.events.push(event);
      else
        groups.push({
          kind: "rows",
          key: `rows-${event.index}`,
          events: [event],
        });
    } else {
      groups.push({ kind: "block", key: `block-${event.index}`, event });
    }
  }
  return groups;
};

// Poll results arrive strictly after afterIndex, but a send response and an
// in-flight poll can race; dedupe by index and keep events ascending.
export const mergeEvents = <T extends { index: number }>(
  existing: T[],
  incoming: T[],
): T[] => {
  const byIndex = new Map<number, T>();
  for (const event of [...existing, ...incoming]) {
    if (!byIndex.has(event.index)) {
      byIndex.set(event.index, event);
    }
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
};

export const highestEventIndex = (events: { index: number }[]): number =>
  events.reduce((max, event) => Math.max(max, event.index), -1);

// A tool call is two events — RUNNING at call time, SUCCEEDED/FAILED when
// the result lands — sharing a callId. Fold them into one chip that changes
// status in place, keeping the RUNNING event's label, id and index (stable
// key). Events without a callId (rows written before it existed, work-label
// chips) keep rendering one chip per event.
export const foldToolCallEvents = <T extends OpusAgentEventLike>(
  events: T[],
): T[] => {
  const folded: T[] = [];
  const chipPosition = new Map<string, number>();
  for (const event of events) {
    const callId = event.payload.toolCall?.callId;
    if (!callId) {
      folded.push(event);
      continue;
    }
    const position = chipPosition.get(callId);
    if (position === undefined) {
      chipPosition.set(callId, folded.length);
      folded.push(event);
      continue;
    }
    const chip = folded[position];
    folded[position] = {
      ...chip,
      payload: {
        ...chip.payload,
        toolCall: {
          ...chip.payload.toolCall!,
          status: event.payload.toolCall!.status,
        },
      },
    };
  }
  return folded;
};

// Display status for a tool-activity chip: a turn that reached a terminal
// status settles any chip still marked RUNNING.
export const resolveToolCallStatus = (
  event: OpusAgentEventLike,
  turnStatus: OpusAgentChatTurnStatus,
): string => {
  const toolCall = event.payload.toolCall;
  if (!toolCall) return "";
  if (toolCall.status !== "RUNNING") return toolCall.status;
  if (turnStatus === OpusAgentChatTurnStatus.Running) return "RUNNING";
  return turnStatus === OpusAgentChatTurnStatus.Succeeded
    ? "SUCCEEDED"
    : "FAILED";
};
